"use client";

import { useEffect } from "react";
import { useRouter } from "@/i18n/navigation";
import { logger } from "@/lib/logger";

/**
 * Registers the Web Push service worker (`/sw.js`) on app load. Rendered once
 * near the root. No-op where service workers aren't supported.
 *
 * Also relays notification clicks: when a notification is clicked while a
 * contact-center tab is already open, the service worker cannot navigate that
 * tab without a reload, so it posts the target URL here and we do a client-side
 * route change instead (keeps React state, in-flight sends and drafts).
 */
export function ServiceWorkerRegister() {
  const router = useRouter();

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type !== "nova-notification-click" || !data.url) return;
      // Same-origin, app-relative paths only — never navigate somewhere a
      // crafted push payload asked for.
      if (!data.url.startsWith("/") || data.url.startsWith("//")) return;
      router.push(data.url);
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [router]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }
    const register = () => {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        .catch((err) => logger.error(err, "service worker registration failed"));
    };
    if (document.readyState === "complete") {
      register();
      return;
    }
    window.addEventListener("load", register, { once: true });
    return () => window.removeEventListener("load", register);
  }, []);

  return null;
}
